'use client'

import {
	createContext,
	type PropsWithChildren,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react'
import {
	getGetAccountPaymentMethodsQueryKey,
	getGetAccountQueryKey,
} from '@cocoonspace/sdk-js/domains/account/account'
import { getGetMeQueryKey } from '@cocoonspace/sdk-js/domains/profile/profile'
import { useQueryClient } from '@tanstack/react-query'
import { getApp, initializeApp } from 'firebase/app'
import { getAuth, onAuthStateChanged, type User } from 'firebase/auth'

import { envClient } from '~/lib/env'

const getAppInstance = () => {
	try {
		return getApp()
	} catch {
		return initializeApp({
			apiKey: envClient.NEXT_PUBLIC_FIREBASE_API_KEY,
			appId: envClient.NEXT_PUBLIC_FIREBASE_APP_ID,
			authDomain: envClient.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
			messagingSenderId: envClient.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
			projectId: envClient.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
			storageBucket: envClient.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
		})
	}
}

export const getAuthInstance = () => getAuth(getAppInstance())

export type AuthDialogTab = 'login' | 'register' | 'forgottenPass'

export interface InitialData {
	email?: string
}

interface AuthContextProps {
	user: User | null
	isLoading: boolean
	isAuth: boolean

	// Auth dialog
	isAuthDialogOpen: boolean
	setIsAuthDialogOpen: (isOpen: boolean) => void

	// Auth dialog
	authDialogActiveTab: AuthDialogTab
	setAuthDialogActiveTab: (tab: AuthDialogTab) => void

	// Auth
	authDialogInitialData: InitialData
	setAuthDialogInitialData: (data: InitialData) => void

	// Auth error msg
	authErrorMsg?: string
	setAuthErrorMsg: (msg: string) => void

	openRegister: () => void
	openLogin: () => void
	openForgetPass: () => void
}

const AuthContext = createContext<AuthContextProps>({
	isAuth: false,
	isLoading: true,
	isAuthDialogOpen: false,
	setIsAuthDialogOpen: () => {},
	user: null,

	authDialogActiveTab: 'login',
	setAuthDialogActiveTab: () => {},

	authDialogInitialData: { email: '' },
	setAuthDialogInitialData: () => {},

	authErrorMsg: '',
	setAuthErrorMsg: () => {},

	openRegister: () => {},
	openLogin: () => {},
	openForgetPass: () => {},
})

export const useAuth = () => {
	const context = useContext(AuthContext)

	if (context === undefined) {
		throw new Error('useAuth must be used within an AuthProvider')
	}

	return context
}

export const AuthProvider = ({ children }: PropsWithChildren) => {
	const queryClient = useQueryClient()
	const [user, setUser] = useState<User | null>(null)
	const [isLoading, setLoading] = useState(true)

	// Auth dialog states
	const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false)

	const [authDialogActiveTab, setAuthDialogActiveTab] =
		useState<AuthDialogTab>('login')

	const [authDialogInitialData, setAuthDialogInitialData] =
		useState<InitialData>({ email: '' })

	const [authErrorMsg, setAuthErrorMsg] = useState('')

	useEffect(() => {
		const auth = getAuthInstance()

		const unsubscribe = onAuthStateChanged(auth, (user) => {
			setUser(user)
			setLoading(false)
		})

		return () => unsubscribe()
	}, [])

	const value = useMemo(
		() => ({
			isAuth: !!user,
			isLoading,
			user,
			isAuthDialogOpen,
			setIsAuthDialogOpen,
			authDialogActiveTab,
			setAuthDialogActiveTab,
			authDialogInitialData,
			setAuthDialogInitialData,
			authErrorMsg,
			setAuthErrorMsg,

			openRegister: () => {
				setAuthDialogActiveTab('register')
				setIsAuthDialogOpen(true)
			},
			openLogin: () => {
				setAuthDialogActiveTab('login')
				setIsAuthDialogOpen(true)
			},
			openForgetPass: () => {
				setAuthDialogActiveTab('forgottenPass')
				setIsAuthDialogOpen(true)
			},
		}),
		[
			user,
			isLoading,
			isAuthDialogOpen,
			authDialogActiveTab,
			authDialogInitialData,
			authErrorMsg,
		],
	)

	useEffect(() => {
		if (user) {
			const toRefetch = [
				getGetMeQueryKey(),
				getGetAccountQueryKey(),
				getGetAccountPaymentMethodsQueryKey(),
			]

			for (const queryKey of toRefetch) {
				queryClient.invalidateQueries({ queryKey })
			}
		}
	}, [user, queryClient])

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
